import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Phone, MessageCircle, CheckCircle, Cpu, Shield, Wifi, Printer, Monitor,
  Wrench, ShieldCheck, Zap, HardDrive, Headphones, MapPin, Clock, Star,
  ChevronDown, ChevronUp, ArrowRight, Home
} from 'lucide-react';

export interface NearbyTown {
  name: string;
  link?: string;
}

export interface InternalLink {
  label: string;
  href: string;
}

export interface LocalSeoPageProps {
  city: string;
  cityShort?: string;
  slug: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  nearbyTowns: NearbyTown[];
  internalLinks: InternalLink[];
  postalCode?: string;
}

const PHONE = '0033626748299';
const PHONE_DISPLAY = '06 26 74 82 99';
const WHATSAPP = 'https://wa.me/33626748299';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.07, duration: 0.45 } }),
};

const FaqItem: React.FC<{ question: string; answer: string }> = ({ question, answer }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border border-gray-700 rounded-xl overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left text-white font-medium hover:bg-[#2F3136] transition-colors"
        onClick={() => setOpen(!open)}
      >
        <span>{question}</span>
        {open ? <ChevronUp className="w-5 h-5 text-blue-400 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-blue-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-4 text-gray-300 text-sm leading-relaxed bg-[#2F3136]">
          {answer}
        </div>
      )}
    </div>
  );
};

const CtaBlock: React.FC<{ city: string }> = ({ city }) => (
  <div className="bg-blue-600/10 border border-blue-500/30 rounded-2xl p-6 my-8 text-center">
    <p className="text-white font-semibold mb-1">Un problème informatique à {city} ?</p>
    <p className="text-gray-400 text-sm mb-4">Diagnostic gratuit — réponse dans l'heure.</p>
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2.5 rounded-xl transition-colors text-sm">
        <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
      </a>
      <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-5 py-2.5 rounded-xl transition-colors text-sm">
        <MessageCircle className="w-4 h-4" /> WhatsApp
      </a>
    </div>
  </div>
);

const LocalSeoPage: React.FC<LocalSeoPageProps> = ({
  city,
  cityShort,
  slug,
  metaTitle,
  metaDescription,
  intro,
  nearbyTowns,
  internalLinks,
  postalCode,
}) => {
  const displayCity = cityShort || city;

  useEffect(() => {
    document.title = metaTitle;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', metaDescription);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://sos-it-services.fr/${slug}`);

    const ogTags: Record<string, string> = {
      'og:title': metaTitle,
      'og:description': metaDescription,
      'og:url': `https://sos-it-services.fr/${slug}`,
      'og:image': 'https://sos-it-services.fr/images/depan-hero.png',
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    window.scrollTo(0, 0);
  }, [metaTitle, metaDescription, slug]);

  const faqs = [
    {
      question: `Combien coûte un dépannage informatique à ${displayCity} ?`,
      answer: `Le diagnostic est toujours gratuit. Le tarif dépend ensuite du type de panne et du mode d'intervention. Un dépannage à distance est généralement moins cher qu'une intervention à domicile. Nous vous donnons un devis clair avant toute intervention, sans mauvaise surprise.`,
    },
    {
      question: 'Pouvez-vous réparer un ordinateur à distance ?',
      answer: `Oui, et c'est notre mode d'intervention le plus rapide. Virus, PC lent, problème Windows, imprimante... Nous prenons le contrôle de votre ordinateur en toute sécurité grâce à un logiciel dédié. Vous voyez tout ce que l'on fait en temps réel. Ce service est disponible partout en France, pas seulement dans le Genevois.`,
    },
    {
      question: `Intervenez-vous dans les communes autour de ${displayCity} ?`,
      answer: `Absolument. Nous intervenons à ${city} et dans toutes les communes voisines : ${nearbyTowns.map(t => t.name).join(', ')}. Pour les interventions à distance, nous pouvons vous aider depuis n'importe où en France.`,
    },
    {
      question: 'Que faire si mon ordinateur est très lent ?',
      answer: `Ne paniquez pas, c'est l'un des problèmes les plus courants et les plus faciles à résoudre. Les causes habituelles sont un disque dur presque plein, des logiciels qui démarrent automatiquement, ou un virus. Contactez-nous pour un diagnostic gratuit et on vous explique la marche à suivre.`,
    },
    {
      question: 'Proposez-vous un diagnostic gratuit ?',
      answer: `Oui, le diagnostic est 100 % gratuit et sans engagement. Décrivez-nous votre problème par WhatsApp ou par téléphone, et nous vous donnons un premier avis immédiatement. Si une intervention est nécessaire, vous recevez un devis clair avant qu'on commence quoi que ce soit.`,
    },
  ];

  return (
    <div className="min-h-screen bg-[#36393F] text-white">

      {/* ── 1. Hero ── */}
      <section className="pt-24 pb-12 px-4 bg-gradient-to-b from-[#2F3136] to-[#36393F]">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial="hidden" animate="visible" custom={0} variants={fadeUp}>
            <span className="inline-flex items-center gap-2 bg-blue-600/20 text-blue-400 text-sm font-medium px-4 py-1.5 rounded-full mb-6">
              <MapPin className="w-4 h-4" /> Service local — {city}{postalCode ? ` (${postalCode})` : ''}
            </span>
          </motion.div>

          <motion.h1
            initial="hidden" animate="visible" custom={1} variants={fadeUp}
            className="text-3xl md:text-5xl font-bold mb-6 leading-tight"
          >
            Dépannage informatique à <span className="text-blue-400">{city}</span>
          </motion.h1>

          <motion.div initial="hidden" animate="visible" custom={2} variants={fadeUp}>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
              <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors">
                <Phone className="w-5 h-5" /> Appeler maintenant
              </a>
              <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors">
                <MessageCircle className="w-5 h-5" /> WhatsApp
              </a>
            </div>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-300">
              {['Diagnostic gratuit', 'Intervention rapide', 'Dépannage à distance partout en France'].map(badge => (
                <span key={badge} className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-green-400" /> {badge}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Contenu principal ── */}
      <div className="max-w-3xl mx-auto px-4 py-12 space-y-16">

        {/* 1. Présentation du service */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} variants={fadeUp}>
          <h2 className="text-2xl md:text-3xl font-bold mb-5">
            Technicien informatique à <span className="text-blue-400">{city}</span>
          </h2>
          <div className="text-gray-300 leading-relaxed space-y-4">
            <p>{intro}</p>
            <p>
              Chez SOS IT Services, nous proposons deux modes d'intervention selon votre situation.
              Le <strong className="text-white">dépannage à distance</strong> permet de résoudre la plupart des pannes
              sans déplacement, en prenant le contrôle de votre ordinateur via un logiciel sécurisé.
              Vous voyez tout ce que le technicien fait en temps réel.
              L'<strong className="text-white">intervention à domicile</strong> est disponible pour les pannes matérielles
              ou si vous préférez la présence physique d'un technicien.
            </p>
            <p>
              Nous intervenons pour les particuliers comme pour les professionnels.
              Que vous ayez besoin d'un dépannage urgent ou d'un suivi régulier,
              notre équipe s'adapte à votre emploi du temps.
              Un devis clair vous est toujours communiqué avant toute intervention.
            </p>
          </div>
          <CtaBlock city={displayCity} />
        </motion.section>

        {/* 2. Problèmes informatiques courants */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} variants={fadeUp}>
          <h2 className="text-2xl md:text-3xl font-bold mb-5">
            Problèmes informatiques courants à {displayCity}
          </h2>
          <div className="text-gray-300 leading-relaxed space-y-4">
            <p>
              Voici les pannes que nos clients à {city} nous signalent le plus fréquemment.
              Dans la grande majorité des cas, elles se résolvent rapidement, à distance ou à domicile.
            </p>
          </div>
          <div className="mt-6 space-y-4">
            {[
              {
                icon: Cpu,
                title: 'Ordinateur lent',
                text: `Votre PC met plusieurs minutes à démarrer ou rame dès que vous ouvrez un programme ? C'est l'une des pannes les plus fréquentes. Les causes sont souvent un disque dur saturé, trop de logiciels au démarrage, ou un virus discret qui consomme vos ressources en arrière-plan. On règle ça en moins d'une heure à distance.`,
              },
              {
                icon: Shield,
                title: 'Virus ou malware',
                text: `Des publicités qui s'ouvrent seules, un navigateur qui redirige vers des sites inconnus, des fichiers qui disparaissent — ce sont les signes d'une infection. Ne touchez à rien et appelez-nous. Nous faisons une analyse complète et nettoyons votre système en profondeur.`,
              },
              {
                icon: Wifi,
                title: 'Problème WiFi',
                text: `Internet qui coupe toutes les 5 minutes, un réseau qui n'apparaît plus, ou une connexion lente malgré un bon débit ? Le problème vient souvent du pilote réseau, de la configuration de votre box ou d'un conflit d'adresse IP. On diagnostique et on règle à distance dans la plupart des cas.`,
              },
              {
                icon: Printer,
                title: 'Imprimante non reconnue',
                text: `Votre imprimante n'est plus détectée après une mise à jour Windows ? C'est un classique. Pilote corrompu, port USB mal configuré, service d'impression bloqué — on connaît ces problèmes par cœur et on les résout rapidement.`,
              },
              {
                icon: Monitor,
                title: 'Windows bloqué ou écran bleu',
                text: `Votre PC affiche un écran bleu au démarrage, tourne en boucle sur les mises à jour, ou reste bloqué sur le logo Windows ? Ces situations font peur mais sont souvent réparables sans perte de données. Contactez-nous avant de tenter une réinstallation.`,
              },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title} className="bg-[#2F3136] rounded-xl p-5 border border-gray-700">
                <div className="flex items-center gap-3 mb-2">
                  <Icon className="w-6 h-6 text-blue-400 flex-shrink-0" />
                  <h3 className="font-semibold text-white">{title}</h3>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* 3. Dépannage à distance */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} variants={fadeUp}>
          <h2 className="text-2xl md:text-3xl font-bold mb-5">
            Dépannage à distance — disponible partout en France
          </h2>
          <div className="text-gray-300 leading-relaxed space-y-4">
            <p>
              Vous habitez à {city} mais vous n'avez pas envie d'attendre qu'un technicien se déplace ?
              Notre service de dépannage à distance est fait pour vous.
              En quelques minutes, nous nous connectons à votre ordinateur via un logiciel sécurisé
              et nous commençons à diagnostiquer le problème.
            </p>
            <p>
              Ce mode d'intervention convient à la majorité des pannes logicielles :
              virus, PC lent, Windows bloqué, problème de mise à jour, imprimante introuvable, email qui ne fonctionne plus...
              Vous restez présent devant votre écran et voyez chaque action en temps réel.
              Si vous avez une question, le technicien vous répond directement.
            </p>
            <p>
              <strong className="text-white">Ce service est disponible partout en France</strong>,
              pas uniquement dans la région du Genevois.
              Que vous soyez à {city}, à Paris ou à Marseille, nous pouvons vous aider sous 48h.
            </p>
          </div>
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            {[
              { icon: Headphones, label: 'Connexion sécurisée', desc: 'Logiciel certifié, session fermée dès la fin' },
              { icon: Clock, label: 'Réponse rapide', desc: 'Intervention le jour même selon disponibilités' },
              { icon: CheckCircle, label: 'Satisfait ou remboursé', desc: 'Paiement uniquement si le problème est résolu' },
            ].map(({ icon: Icon, label, desc }) => (
              <div key={label} className="bg-[#2F3136] rounded-xl p-4 border border-gray-700 text-center">
                <Icon className="w-7 h-7 text-blue-400 mx-auto mb-2" />
                <p className="text-white font-medium text-sm">{label}</p>
                <p className="text-gray-400 text-xs mt-1">{desc}</p>
              </div>
            ))}
          </div>
          <CtaBlock city={displayCity} />
        </motion.section>

        {/* 4. Intervention à domicile */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} variants={fadeUp}>
          <h2 className="text-2xl md:text-3xl font-bold mb-5">
            Intervention à domicile à {city}
          </h2>
          <div className="text-gray-300 leading-relaxed space-y-4">
            <p>
              Certaines pannes nécessitent une présence physique : remplacement de composant,
              installation d'imprimante, configuration réseau, ou simplement si vous préférez
              qu'un technicien soit à vos côtés pour vous expliquer ce qui est fait.
            </p>
            <p>
              Nous intervenons à domicile à {city} et dans un rayon de 50 km autour de Genève,
              couvrant l'essentiel de la Haute-Savoie et du Pays de Gex.
              Le déplacement est inclus dans le tarif, sans frais cachés.
              Nous nous adaptons à vos disponibilités, y compris le soir ou le week-end selon notre planning.
            </p>
            <p>
              Nos services à domicile couvrent notamment :
            </p>
            <ul className="space-y-2 mt-2">
              {[
                { icon: Wrench, text: 'Dépannage et réparation ordinateur (PC, Mac, portable)' },
                { icon: ShieldCheck, text: 'Suppression de virus et sécurisation de votre système' },
                { icon: Zap, text: 'Optimisation et accélération de votre PC' },
                { icon: HardDrive, text: 'Récupération de données sur disque dur en panne' },
                { icon: Home, text: 'Configuration réseau, box internet et WiFi' },
              ].map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-2 text-gray-300 text-sm">
                  <Icon className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  {text}
                </li>
              ))}
            </ul>
          </div>
        </motion.section>

        {/* 5. Villes proches */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} variants={fadeUp}>
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            J'interviens également dans les villes proches de {displayCity}
          </h2>
          <p className="text-gray-400 mb-6">
            Vous habitez dans une commune voisine ? Pas de problème.
            Voici les villes où nous intervenons régulièrement autour de {city} :
          </p>
          <div className="flex flex-wrap gap-3 mb-8">
            {nearbyTowns.map(({ name, link }) => (
              link ? (
                <Link
                  key={name}
                  to={link}
                  className="flex items-center gap-1.5 bg-[#2F3136] hover:bg-blue-600/20 border border-gray-700 hover:border-blue-500 text-gray-300 hover:text-white px-4 py-2 rounded-full text-sm transition-colors"
                >
                  <MapPin className="w-3.5 h-3.5 text-blue-400" /> {name}
                </Link>
              ) : (
                <span key={name} className="flex items-center gap-1.5 bg-[#2F3136] border border-gray-700 text-gray-400 px-4 py-2 rounded-full text-sm">
                  <MapPin className="w-3.5 h-3.5 text-blue-400" /> {name}
                </span>
              )
            ))}
          </div>
          {internalLinks.length > 0 && (
            <div className="space-y-2">
              {internalLinks.map(({ label, href }) => (
                <Link
                  key={href}
                  to={href}
                  className="flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm transition-colors"
                >
                  <ArrowRight className="w-4 h-4 flex-shrink-0" /> {label}
                </Link>
              ))}
            </div>
          )}
        </motion.section>

        {/* FAQ */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} variants={fadeUp}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6">
            Questions fréquentes — dépannage informatique à {displayCity}
          </h2>
          <div className="space-y-3">
            {faqs.map(faq => (
              <FaqItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </motion.section>

      </div>

      {/* CTA final */}
      <section className="py-16 px-4 bg-gradient-to-b from-[#2F3136] to-[#1e2124]">
        <div className="max-w-2xl mx-auto text-center">
          <Star className="w-8 h-8 text-blue-400 mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-bold mb-3">
            Votre ordinateur pose problème à {city} ?
          </h2>
          <p className="text-gray-400 mb-6">
            Contactez-nous maintenant. Diagnostic gratuit, réponse dans l'heure,
            intervention à distance ou à domicile selon votre besoin.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-xl transition-colors">
              <Phone className="w-5 h-5" /> {PHONE_DISPLAY}
            </a>
            <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-3 rounded-xl transition-colors">
              <MessageCircle className="w-5 h-5" /> WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LocalSeoPage;
